<template>
  <header class="bg-white shadow-sm sticky top-0 z-40">
    <div class="container-custom">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link to="/" class="flex items-center space-x-2">
          <!-- Logo Image -->
          <img
            v-if="companyStore.company?.logo_url"
            :src="getImageUrl(companyStore.company.logo_url)"
            :alt="companyStore.company?.name || 'Logo'"
            class="h-10 w-auto max-w-[100px] object-contain"
          >
          <!-- Fallback Logo -->
          <template v-else>
            <div 
              class="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-xl bg-orange-500"
            >
              {{ companyStore.company?.name?.charAt(0) || 'D' }}
            </div>
            <span class="font-semibold text-gray-900 hidden sm:block">
              {{ companyStore.company?.name || 'Delivery' }}
            </span>
          </template>
        </router-link>
        
        <!-- Navigation Desktop -->
        <nav class="hidden md:flex items-center space-x-8">
          <router-link 
            to="/" 
            class="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            :class="{ 'text-orange-600': $route.name === 'home' }"
          >
            {{ $t('nav.home') }}
          </router-link>
          <router-link 
            to="/menu" 
            class="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            :class="{ 'text-orange-600': $route.name === 'menu' }"
          >
            {{ $t('nav.menu') }}
          </router-link>
          <router-link 
            to="/order/track" 
            class="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            :class="{ 'text-orange-600': $route.name === 'order-track' }"
          >
            {{ $t('nav.orders') }}
          </router-link>
        </nav>
        
        <!-- Actions -->
        <div class="flex items-center space-x-4">
          <!-- Language Selector -->
          <LanguageSelector />
          
          <!-- Cart Button Desktop -->
          <router-link 
            to="/cart"
            class="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ShoppingCartIcon class="h-6 w-6" />
            <span 
              v-if="cartStore.itemCount > 0"
              class="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-medium text-white flex items-center justify-center bg-orange-500"
            >
              {{ cartStore.itemCount > 9 ? '9+' : cartStore.itemCount }}
            </span>
          </router-link>
          
          <!-- Mobile Menu Button -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden p-2 text-gray-600 hover:text-gray-900"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="h-6 w-6" />
            <XMarkIcon v-else class="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
    
    <!-- Mobile Menu -->
    <div v-if="mobileMenuOpen" class="md:hidden border-t">
      <nav class="px-4 py-2 space-y-1">
        <router-link
          to="/"
          @click="mobileMenuOpen = false"
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
          :class="{ 'bg-orange-50 text-orange-600': $route.name === 'home' }"
        >
          <HomeIcon class="h-6 w-6" />
          <span>{{ $t('nav.home') }}</span>
        </router-link>
        <router-link
          to="/menu"
          @click="mobileMenuOpen = false"
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
          :class="{ 'bg-orange-50 text-orange-600': $route.name === 'menu' }"
        >
          <BookOpenIcon class="h-6 w-6" />
          <span>{{ $t('nav.menu') }}</span>
        </router-link>
        <router-link
          to="/cart"
          @click="mobileMenuOpen = false"
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
          :class="{ 'bg-orange-50 text-orange-600': $route.name === 'cart' }"
        >
          <ShoppingCartIcon class="h-6 w-6" />
          <span>{{ $t('nav.cart') }}</span>
          <span 
            v-if="cartStore.itemCount > 0"
            class="ml-auto h-5 w-5 rounded-full text-xs font-medium text-white flex items-center justify-center bg-orange-500"
          >
            {{ cartStore.itemCount }}
          </span>
        </router-link>
        <router-link
          to="/order/track"
          @click="mobileMenuOpen = false"
          class="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
          :class="{ 'bg-orange-50 text-orange-600': $route.name === 'order-track' }"
        >
          <TruckIcon class="h-6 w-6" />
          <span>{{ $t('nav.orders') }}</span>
        </router-link>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  ShoppingCartIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BookOpenIcon,
  TruckIcon
} from '@heroicons/vue/24/outline'
import { useCartStore, useCompanyStore } from '@/stores'
import LanguageSelector from '@/components/common/LanguageSelector.vue'

const cartStore = useCartStore()
const companyStore = useCompanyStore()

const mobileMenuOpen = ref(false)

const getImageUrl = (url: string | undefined) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}
</script>
